import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import '../assets/styles/AchievementShowcase.css';

const AchievementShowcase = ({ kidUserId }) => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, earned, locked

  const fetchAchievements = useCallback(async () => {
    try {
      const response = await axios.get(`/api/v1/kid-achievements?kid_user_id=${kidUserId}`);
      setAchievements(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch achievements:', err);
      setError('Failed to load achievements');
    } finally {
      setLoading(false);
    }
  }, [kidUserId]);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);


  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'gold': return '#fbbf24';
      case 'silver': return '#94a3b8';
      case 'bronze': return '#b45309';
      default: return '#f59e0b';
    }
  };

  const getProgressPercentage = (achievement) => {
    if (achievement.earned) return 100;
    if (!achievement.achievement.requirement_value) return 0;
    return Math.min(100, (achievement.progress / achievement.achievement.requirement_value) * 100);
  };

  const filteredAchievements = achievements.filter(ach => {
    if (filter === 'earned') return ach.earned;
    if (filter === 'locked') return !ach.earned;
    return true;
  });

  const earnedCount = achievements.filter(a => a.earned).length;
  const totalCount = achievements.length;

  if (loading) {
    return <div className="achievement-loading">Loading achievements...</div>;
  }

  return (
    <div className="achievement-showcase">
      <div className="achievement-header">
        <div className="header-info">
          <h2>🏆 Achievements</h2>
          <div className="achievement-stats">
            <span className="stat-earned">{earnedCount}</span>
            <span className="stat-separator">/</span>
            <span className="stat-total">{totalCount}</span>
            <span className="stat-label">Unlocked</span>
          </div>
        </div>
        <div className="achievement-filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filter === 'earned' ? 'active' : ''}`}
            onClick={() => setFilter('earned')}
          >
            Earned
          </button>
          <button
            className={`filter-btn ${filter === 'locked' ? 'active' : ''}`}
            onClick={() => setFilter('locked')}
          >
            Locked
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {filteredAchievements.length === 0 ? (
        <div className="no-achievements">
          <p>No achievements to display</p>
          {filter === 'earned' && <p>Keep working to earn your first achievement!</p>}
        </div>
      ) : (
        <div className="achievements-grid">
          {filteredAchievements.map(item => (
            <div
              key={item.achievement.id}
              className={`achievement-card ${item.earned ? 'earned' : 'locked'}`}
            >
              <div className="achievement-icon-wrapper">
                <div
                  className={`achievement-icon tier-${item.achievement.tier || 'bronze'}`}
                  style={{
                    borderColor: getTierColor(item.achievement.tier),
                    boxShadow: item.earned ? `0 0 15px ${getTierColor(item.achievement.tier)}40` : 'none'
                  }}
                >
                  {item.achievement.icon}
                </div>
                {item.earned && (
                  <div className="earned-badge">✓</div>
                )}
              </div>

              <div className="achievement-content">
                <div className="achievement-category" style={{ color: getTierColor(item.achievement.tier) }}>
                  {item.achievement.tier ? item.achievement.tier.toUpperCase() : 'BRONZE'} • {item.achievement.category.toUpperCase()}
                </div>
                <h3 className="achievement-name">{item.achievement.name}</h3>
                <p className="achievement-description">{item.achievement.description}</p>

                {!item.earned && item.achievement.requirement_value && (
                  <div className="achievement-progress">
                    <div className="progress-bar-bg">
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${getProgressPercentage(item)}%`,
                          backgroundColor: getTierColor(item.achievement.tier)
                        }}
                      />
                    </div>
                    <div className="progress-text">
                      {item.progress} / {item.achievement.requirement_value}
                    </div>
                  </div>
                )}

                {item.earned && item.earned_at && (
                  <div className="earned-date">
                    Earned {new Date(item.earned_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                )}

                {item.achievement.reward_type && (
                  <div className="achievement-reward">
                    <span className="reward-icon">🎁</span>
                    <span className="reward-text">
                      Unlocks {item.achievement.reward_type}: {item.achievement.reward_value}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {earnedCount > 0 && (
        <div className="achievement-summary">
          <div className="summary-card">
            <div className="summary-icon">🎯</div>
            <div className="summary-content">
              <h3>Keep Going!</h3>
              <p>You've unlocked {earnedCount} out of {totalCount} achievements. {totalCount - earnedCount} more to go!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementShowcase;
